import { useState, useEffect, useCallback } from 'react';
import { useAppStore } from '../store/useAppStore';
import { getUserAssets, supabase } from '../utils/supabaseStorage';

interface Asset {
  id: string;
  hy_user_id: string;
  public_url: string;
  isaigenerated: boolean;
  created_at: string;
  updated_at: string;
  filename?: string;
  file_size?: number;
  media_type?: 'image' | 'video' | 'audio';
  file_extension?: string;
  fal_request_id?: string;
  generation_prompt?: string;
  generation_params?: any;
  model?: string;
  status?: string;
  project_id?: string | null;
}

export const useUserAssets = (isAIGenerated?: boolean) => {
  const { session } = useAppStore();
  const hyUserId = session?.hy_user_id;
  const origSessionId = session?.original_session_id;
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAssets = useCallback(async () => {
    console.log('🔍 [ASSETS] Fetching assets for session type:', origSessionId === 'mock' ? 'MOCK' : 'REAL');
    console.log('🆔 [ASSETS] User ID from session:', hyUserId);

    if (!hyUserId) {
      console.warn('⚠️ [ASSETS] No session or hy_user_id found - cannot fetch assets');
      setLoading(false);
      setError('Session not available - cannot fetch assets');
      return;
    }

    const isRealSession = origSessionId !== 'mock';
    const isMockSession = origSessionId === 'mock';

    if (isRealSession && hyUserId.trim() === '') {
      console.error('❌ [ASSETS] Real session must have valid hy_user_id');
      setLoading(false);
      setError('Invalid session - user ID required');
      return;
    }

    if (isRealSession && hyUserId === 'FTYVyc9tpPXR6QodX4joUSOCHDN2') {
      console.error('❌ [ASSETS] CRITICAL: Real session is using mock user ID!');
      setLoading(false);
      setError('Session validation error - please restart the app');
      return;
    }

    if (isMockSession) {
      console.log('🧪 [ASSETS] Using mock session - will fetch mock user assets');
    } else {
      console.log('🔐 [ASSETS] Using real session - fetching assets for real user:', hyUserId);
    }
    try {
      setLoading(true);
      setError(null);

      console.log(`📤 [ASSETS] Calling getUserAssets for ${isRealSession ? 'REAL' : 'MOCK'} session:`, {
        userId: hyUserId,
        isAIGenerated,
        limit: 50,
        offset: 0,
        sessionType: isRealSession ? 'real' : 'mock',
      });

      const result = await getUserAssets(hyUserId, isAIGenerated, 50, 0);

      console.log('📥 [ASSETS] getUserAssets result:', result);

      if (result.success && result.assets) {
        console.log(`✅ [ASSETS] Loaded ${result.assets.length} assets for user ${hyUserId}`);
        setAssets(result.assets);
      } else {
        console.error('❌ [ASSETS] Failed to fetch assets:', result.error);
        setError(result.error || 'Failed to fetch assets');
        setAssets([]);
      }
    } catch (err) {
      console.error('💥 [ASSETS] Exception while fetching assets:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, [hyUserId, origSessionId, isAIGenerated]);

  // Manual check status for a specific asset
  const checkAssetStatus = async (): Promise<{ success: boolean; error?: string }> => {
    // Background processing assets are handled by the polling worker
    // Manual status checking is disabled for background generation
    return {
      success: false,
      error:
        'Processing finishes when FAL posts to our webhook (or the polling worker runs). Your library refreshes automatically when the file is ready.',
    };
  };

  useEffect(() => {
    console.log('🔄 [ASSETS] useEffect triggered - session changed:', {
      hasSession: !!hyUserId,
      userId: hyUserId,
      isAIGenerated,
    });

    if (hyUserId) {
      void fetchAssets();
    }
  }, [hyUserId, fetchAssets]);

  /** Same-tab refreshes (e.g. after generation registers a placeholder). */
  useEffect(() => {
    const onRefresh = () => {
      if (hyUserId) void fetchAssets();
    };
    window.addEventListener('refreshAssetGallery', onRefresh);
    return () => window.removeEventListener('refreshAssetGallery', onRefresh);
  }, [hyUserId, fetchAssets]);

  /**
   * When FAL calls fal-queue-webhook, the asset row updates — notify all gallery consumers.
   * Enable Realtime for table `public.assets` in Supabase Dashboard if events do not arrive.
   */
  useEffect(() => {
    const uid = hyUserId;
    if (!uid || origSessionId === 'mock') return;

    const channel = supabase
      .channel(`assets-changes-${uid}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'assets',
          filter: `hy_user_id=eq.${uid}`,
        },
        () => {
          window.dispatchEvent(new CustomEvent('refreshAssetGallery'));
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [hyUserId, origSessionId]);

  /** Fallback if Realtime is off: poll while placeholders are still processing. */
  useEffect(() => {
    const hasProcessing = assets.some(
      (a) => a.status === 'processing' || a.status === 'pending'
    );
    if (!hasProcessing || !hyUserId) return;
    const id = window.setInterval(() => {
      void fetchAssets();
    }, 10_000);
    return () => window.clearInterval(id);
  }, [assets, hyUserId, fetchAssets]);

  return { assets, loading, error, refetch: fetchAssets, checkAssetStatus };
}